import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Divider,
  Alert,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Image as ImageIcon,
  Visibility as PreviewIcon,
  VisibilityOff as PreviewOffIcon,
  ColorLens as ColorLensIcon,
  FontDownload as FontIcon,
} from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';

import { StoreFormData } from './types/store.types';
import ImageUpload from './components/ImageUpload';

interface StorePageFormProps {
  previewMode?: boolean;
  togglePreviewMode?: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`store-tabpanel-${index}`}
      aria-labelledby={`store-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const StorePageForm: React.FC<StorePageFormProps> = ({
  previewMode = false,
  togglePreviewMode,
}) => {
  const { register, watch, setValue, formState: { errors } } = useFormContext<StoreFormData>();
  const [tabValue, setTabValue] = useState(0);

  // Cores predefinidas
  const predefinedColors = [
    '#1976d2', // Azul
    '#dc004e', // Rosa
    '#2e7d32', // Verde
    '#ed6c02', // Laranja
    '#9c27b0', // Roxo
    '#d32f2f', // Vermelho
    '#0288d1', // Azul claro
    '#7b1fa2', // Roxo escuro
    '#388e3c', // Verde escuro
    '#f57c00', // Laranja escuro
    '#5d4037', // Marrom
    '#455a64', // Cinza azulado
  ];

  // Fontes disponíveis
  const availableFonts = [
    { value: 'Roboto', label: 'Roboto' },
    { value: 'Open Sans', label: 'Open Sans' },
    { value: 'Lato', label: 'Lato' },
    { value: 'Montserrat', label: 'Montserrat' },
    { value: 'Poppins', label: 'Poppins' },
    { value: 'Nunito', label: 'Nunito' },
    { value: 'Inter', label: 'Inter' },
    { value: 'Source Sans Pro', label: 'Source Sans Pro' },
  ];

  const watchedTheme = watch('customTheme') || {
    primaryColor: '#1976d2',
    secondaryColor: '#dc004e',
    fontFamily: 'Roboto',
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleColorChange = (colorType: 'primaryColor' | 'secondaryColor', color: string) => {
    setValue('customTheme', {
      ...watchedTheme,
      [colorType]: color,
    });
  };

  const handleFontChange = (font: string) => {
    setValue('customTheme', {
      ...watchedTheme,
      fontFamily: font,
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaletteIcon color="primary" />
          Personalização da Loja
        </Typography>
        
        {togglePreviewMode && (
          <Button
            variant="outlined"
            startIcon={previewMode ? <PreviewOffIcon /> : <PreviewIcon />}
            onClick={togglePreviewMode}
          >
            {previewMode ? 'Fechar Preview' : 'Preview'}
          </Button>
        )}
      </Box>

      <Paper sx={{ width: '100%' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="configurações da loja">
          <Tab label="Visual" />
          <Tab label="Imagens" />
          <Tab label="Funcionalidades" />
        </Tabs>

        {/* Aba Visual */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Cores */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ColorLensIcon color="primary" />
                Cores da Loja
              </Typography>
            </Grid>

            {/* Cor Primária */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Cor Primária
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Cor principal usada em botões, links e elementos de destaque
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <TextField
                      value={watchedTheme.primaryColor}
                      onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                      label="Código da cor"
                      size="small"
                      sx={{ flexGrow: 1 }}
                    />
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: watchedTheme.primaryColor,
                        borderRadius: 1,
                        border: '1px solid #ccc',
                      }}
                    />
                  </Box>

                  <Typography variant="body2" gutterBottom>
                    Cores predefinidas:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {predefinedColors.map((color) => (
                      <Box
                        key={color}
                        sx={{
                          width: 30,
                          height: 30,
                          backgroundColor: color,
                          borderRadius: 1,
                          cursor: 'pointer',
                          border: watchedTheme.primaryColor === color ? '2px solid #000' : '1px solid #ccc',
                          '&:hover': {
                            transform: 'scale(1.1)',
                          },
                        }}
                        onClick={() => handleColorChange('primaryColor', color)}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Cor Secundária */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Cor Secundária
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Cor usada em elementos secundários e destaques
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <TextField
                      value={watchedTheme.secondaryColor}
                      onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                      label="Código da cor"
                      size="small"
                      sx={{ flexGrow: 1 }}
                    />
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: watchedTheme.secondaryColor,
                        borderRadius: 1,
                        border: '1px solid #ccc',
                      }}
                    />
                  </Box>

                  <Typography variant="body2" gutterBottom>
                    Cores predefinidas:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {predefinedColors.map((color) => (
                      <Box
                        key={color}
                        sx={{
                          width: 30,
                          height: 30,
                          backgroundColor: color,
                          borderRadius: 1,
                          cursor: 'pointer',
                          border: watchedTheme.secondaryColor === color ? '2px solid #000' : '1px solid #ccc',
                          '&:hover': {
                            transform: 'scale(1.1)',
                          },
                        }}
                        onClick={() => handleColorChange('secondaryColor', color)}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Fonte */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FontIcon color="primary" />
                    Fonte da Loja
                  </Typography>
                  
                  <FormControl fullWidth sx={{ maxWidth: 300 }}>
                    <InputLabel>Fonte</InputLabel>
                    <Select
                      value={watchedTheme.fontFamily}
                      label="Fonte"
                      onChange={(e) => handleFontChange(e.target.value)}
                    >
                      {availableFonts.map((font) => (
                        <MenuItem key={font.value} value={font.value} sx={{ fontFamily: font.value }}>
                          {font.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Preview da fonte:
                    </Typography>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontFamily: watchedTheme.fontFamily,
                        color: watchedTheme.primaryColor,
                      }}
                    >
                      Minha Loja Online
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontFamily: watchedTheme.fontFamily,
                        color: 'text.primary',
                      }}
                    >
                      Este é um exemplo de como o texto aparecerá na sua loja.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Aba Imagens */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            {/* Logo da Loja */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ImageIcon color="primary" />
                    Logo da Loja
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Upload do logo que aparecerá no cabeçalho da sua loja
                  </Typography>
                  
                  <ImageUpload
                    onImageChange={(file) => setValue('logo', file as File)}
                    accept="image/*"
                    maxSize={5 * 1024 * 1024} // 5MB
                    preview
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Imagem de Capa */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Imagem de Capa
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Imagem que aparecerá no topo da página da loja
                  </Typography>
                  
                  <ImageUpload
                    onImageChange={(file) => setValue('coverImage', file as File)}
                    accept="image/*"
                    maxSize={10 * 1024 * 1024} // 10MB
                    preview
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Galeria de Imagens */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Galeria de Imagens
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Adicione imagens para criar uma galeria na sua loja
                  </Typography>
                  
                  <ImageUpload
                    onImageChange={(files) => setValue('galleryImages', files as File[])}
                    accept="image/*"
                    maxSize={10 * 1024 * 1024} // 10MB
                    multiple
                    preview
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Aba Funcionalidades */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            {/* Funcionalidades da Página */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Funcionalidades da Página
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={watch('showMap') || false}
                    onChange={(e) => setValue('showMap', e.target.checked)}
                  />
                }
                label="Mostrar Mapa"
              />
              <Typography variant="body2" color="text.secondary">
                Exibe um mapa com a localização da loja
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={watch('showSocialShare') || false}
                    onChange={(e) => setValue('showSocialShare', e.target.checked)}
                  />
                }
                label="Botões de Compartilhamento"
              />
              <Typography variant="body2" color="text.secondary">
                Adiciona botões para compartilhar nas redes sociais
              </Typography>
            </Grid>

            {/* Configurações de SEO */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Configurações de SEO
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Título da Página (SEO)"
                placeholder="Minha Loja Online - Produtos Incríveis"
                {...register('seo.metaTitle')}
                helperText="Título que aparecerá nos resultados de busca"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Descrição (SEO)"
                multiline
                rows={3}
                placeholder="Descrição da sua loja para motores de busca..."
                {...register('seo.metaDescription')}
                helperText="Descrição que aparecerá nos resultados de busca"
              />
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* Resumo da Personalização */}
      <Card sx={{ mt: 3, bgcolor: 'grey.50' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Resumo da Personalização
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" gutterBottom>
                Cor Primária:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    backgroundColor: watchedTheme.primaryColor,
                    borderRadius: 1,
                    border: '1px solid #ccc',
                  }}
                />
                <Typography variant="body2">{watchedTheme.primaryColor}</Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" gutterBottom>
                Cor Secundária:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    backgroundColor: watchedTheme.secondaryColor,
                    borderRadius: 1,
                    border: '1px solid #ccc',
                  }}
                />
                <Typography variant="body2">{watchedTheme.secondaryColor}</Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" gutterBottom>
                Fonte:
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: watchedTheme.fontFamily }}>
                {watchedTheme.fontFamily}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" gutterBottom>
                Funcionalidades:
              </Typography>
              <Typography variant="body2">
                {watch('showMap') ? 'Mapa ✓' : 'Mapa ✗'} | {' '}
                {watch('showSocialShare') ? 'Compartilhamento ✓' : 'Compartilhamento ✗'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Dicas */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom color="primary">
          💡 Dicas para personalização:
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • Escolha cores que reflitam a identidade da sua marca<br />
          • Use fontes legíveis e profissionais<br />
          • Otimize as imagens para web (formato WebP recomendado)<br />
          • Configure SEO para melhor visibilidade nas buscas<br />
          • Teste como sua loja aparece em diferentes dispositivos
        </Typography>
      </Box>
    </Box>
  );
};

export default StorePageForm;
